import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useData } from "./useData";
import { useGlobalDateRange } from "./useGlobalDateRange";
import { supabase } from "../lib/supabase";
import { coalesceRpc } from "../lib/rpcCoalescer";
import { toOrgStartISO, toOrgEndISO, toOrgDateKey } from "../lib/timezone";
import { logger } from "../lib/logger";
import {
  pickChartGranularity,
  aggregateDailySeries,
  type DailyPoint,
} from "../utils/chartGranularity";

// =============================================================================
// useOperationsAnalytics - THE ANALYTICS ENGINE
// =============================================================================
// PURPOSE: Computes 40+ dashboard metrics with date ranges, sparklines, percent changes.
//
// REQUIREMENTS:
// - Filter by organization_id and apply RBAC (tunnel vision for closers/setters/dialers)
// - Calculate historical comparison (previous period) for percent change indicators
// - Generate sparkline data for ticket tier trends (last 30 days)
// - Fetch Instagram DM stats via RPC (get_dm_analytics_fast)
// - Support role-specific metric sets (exec sees all, closer sees cash focus, setter sees quality)
//
// KEY DECISIONS:
// - Date ranges use timezone-aware conversion (org.timezone)
// - Callsbooked = ALL sales_call_outcomes (including no-shows)
// - CallsTaken = Only calls where lead showed up
// - AOV = High ticket deals only (excludes low ticket)
// - DM metrics use get_dm_analytics_fast RPC (pre-aggregated daily_dm_metrics table)
// - Sparklines aggregate by day, sorted chronologically
// - AbortController prevents race conditions on org switching
// =============================================================================

import { CACHE_VERSION } from "../lib/cacheVersion";

const OPS_DM_CACHE_PREFIX = `altara_${CACHE_VERSION}_ops_dm_`;
const OPS_DM_CACHE_TTL = 10 * 60 * 1000; // 10 minutes - live ranges
const OPS_DM_HISTORICAL_TTL = 24 * 60 * 60 * 1000; // 24 hours - historical

interface DmCacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

function getOpsDmCache<T>(key: string, orgId: string): T | null {
  try {
    const raw = localStorage.getItem(OPS_DM_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: DmCacheEntry<T> = JSON.parse(raw);
    // Check if date range in key is historical (ends before today)
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})T/g);
    let ttl = OPS_DM_CACHE_TTL;
    if (dateMatch && dateMatch.length >= 2) {
      const endDateStr = dateMatch[1].replace("T", "");
      const endDate = new Date(endDateStr + "T23:59:59");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate < today) ttl = OPS_DM_HISTORICAL_TTL;
    }
    if (Date.now() - entry.timestamp > ttl || entry.orgId !== orgId) {
      localStorage.removeItem(OPS_DM_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setOpsDmCache<T>(key: string, data: T, orgId: string): void {
  try {
    localStorage.setItem(
      OPS_DM_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgId })
    );
  } catch {
    /* localStorage full — ignore */
  }
}

// --- Server-side KPI data (from get_operations_kpis RPC) ---
interface RpcKpiData {
  cash_collected: number;
  revenue_closed: number;
  calls_booked: number;
  // Right-censored pre-call count for cashPerCall denominator (excludes future-scheduled).
  // Mako May 13, 2026 lock. Equals calls_booked for fully-past windows.
  calls_booked_settled: number;
  setter_booked: number;
  closer_booked: number;
  pre_calls_made: number;
  cancellations: number;
  cancellations_raw: number;
  deals_closed: number;
  lost: number;
  no_shows: number;
  rescheduled: number;
  // pending_close (Pending Close, Jun 2026) — show-not-win. Optional+nullable:
  // localStorage may serve a pre-deploy RpcKpiData cached without the key,
  // and the RPC/TS can land at different moments. Consumers use `?? 0`.
  pending_close?: number | null;
  ht_deals: number;
  ht_cash: number;
  ht_revenue: number;
  lt_deals: number;
  lt_cash: number;
  lt_revenue: number;
  installments_count: number;
  installments_cash: number;
  // Show-up Rate (Mako May 13, 2026 lock): orphan pre-calls eat show-rate denominator
  orphan_pre_calls: number;
  orphan_rate: number;
}

interface DailyMetricRow {
  date: string;
  cash_collected: number;
  revenue_closed: number;
  high_ticket_cash: number;
  low_ticket_cash: number;
  installments_collected: number;
}

const OPS_KPI_CACHE_PREFIX = `altara_${CACHE_VERSION}_ops_kpi_`;

function getOpsKpiCache(key: string, orgId: string): RpcKpiData | null {
  try {
    const raw = localStorage.getItem(OPS_KPI_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: DmCacheEntry<RpcKpiData> = JSON.parse(raw);
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})T/g);
    let ttl = OPS_DM_CACHE_TTL;
    if (dateMatch && dateMatch.length >= 2) {
      const endDateStr = dateMatch[1].replace("T", "");
      const endDate = new Date(endDateStr + "T23:59:59");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate < today) ttl = OPS_DM_HISTORICAL_TTL;
    }
    if (Date.now() - entry.timestamp > ttl || entry.orgId !== orgId) {
      localStorage.removeItem(OPS_KPI_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setOpsKpiCache(key: string, data: RpcKpiData, orgId: string): void {
  try {
    localStorage.setItem(
      OPS_KPI_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgId })
    );
  } catch {
    /* localStorage full — ignore */
  }
}

// --- Daily org metrics cache (FinancialGraph data — was uncached, caused blank chart on date change) ---
const OPS_DAILY_CACHE_PREFIX = `altara_${CACHE_VERSION}_ops_daily_`;

function getOpsDailyCache(key: string, orgId: string): DailyMetricRow[] | null {
  try {
    const raw = localStorage.getItem(OPS_DAILY_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: DmCacheEntry<DailyMetricRow[]> = JSON.parse(raw);
    // Historical ranges = 24h TTL, live ranges = 10 min
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})/g);
    let ttl = OPS_DM_CACHE_TTL;
    if (dateMatch && dateMatch.length >= 2) {
      const endDate = new Date(dateMatch[1] + "T23:59:59");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate < today) ttl = OPS_DM_HISTORICAL_TTL;
    }
    if (Date.now() - entry.timestamp > ttl || entry.orgId !== orgId) {
      localStorage.removeItem(OPS_DAILY_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setOpsDailyCache(key: string, data: DailyMetricRow[], orgId: string): void {
  try {
    localStorage.setItem(
      OPS_DAILY_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgId })
    );
  } catch {
    /* localStorage full — ignore */
  }
}

// Derive rates from raw RPC data (formulas live here, not in SQL)
// Show-up Rate (Mako May 13, 2026 lock): Shows / (Shows + SCO no_shows + orphan pre-calls).
// cashPerBooked denominator uses calls_booked_settled (right-censored, future-scheduled
// pre-calls excluded) so the ratio doesn't deflate on in-flight ranges.
function deriveKpiRates(r: RpcKpiData) {
  // Shows = PIF + CPP + Lost + Rescheduled + Pending Close (Jun 2026 — a
  // pending counts as a show the moment it is filed, NEVER a win).
  // r.deals_closed is the RPC's pre-summed PIF+CPP win count — do NOT
  // decompose it; only APPEND the pending term.
  const shows = r.deals_closed + r.lost + r.rescheduled + (r.pending_close ?? 0);
  // decisiveCalls = shows MINUS rescheduled (R21/R24, Mako 2026-08-09). A reschedule is
  // attendance without a decision, so it counts for SHOW rate and not for CLOSE rate.
  // These are deliberately different populations — do not collapse them.
  const decisiveCalls = shows - r.rescheduled;
  const showUpDenom = shows + r.no_shows + (r.orphan_pre_calls ?? 0);
  const showUpRate = showUpDenom > 0 ? (shows / showUpDenom) * 100 : 0;
  // noShowRate + orphanRate share showUpDenom so the three slices sum to 100 on
  // the card. The RPC's own orphan_rate field divides by distinct pre-call leads,
  // which only coincides with showUpDenom under the precall-bound rule (d) —
  // derive from showUpDenom here so the identity survives any RPC drift.
  const noShowRate = showUpDenom > 0 ? (r.no_shows / showUpDenom) * 100 : 0;
  const orphanShareRate = showUpDenom > 0 ? ((r.orphan_pre_calls ?? 0) / showUpDenom) * 100 : 0;
  // closeRate is the FINAL close rate (R29/R30): money that landed, PIF + CPP only, over
  // decisiveCalls. This is what gates and prestige score on. pendingCloseRate carries the
  // provisional version and is never scored.
  const closeRate = decisiveCalls > 0 ? (r.deals_closed / decisiveCalls) * 100 : 0;
  const pendingCloseRate =
    decisiveCalls > 0 ? ((r.deals_closed + (r.pending_close ?? 0)) / decisiveCalls) * 100 : 0;
  const aov = r.ht_deals > 0 ? r.ht_cash / r.ht_deals : 0;
  const cashPerShow = shows > 0 ? r.cash_collected / shows : 0;
  const settledBookings = r.calls_booked_settled ?? r.calls_booked;
  const cashPerBooked = settledBookings > 0 ? r.cash_collected / settledBookings : 0;
  return {
    shows,
    decisiveCalls,
    showUpRate,
    noShowRate,
    orphanShareRate,
    closeRate,
    pendingCloseRate,
    aov,
    cashPerShow,
    cashPerBooked,
  };
}

// --- Ticket Tier Breakdown ---
export interface TicketTier {
  cash: number;
  revenue: number;
  deals: number;
  percentChange: number;
  sparkline: number[];
}

// --- Ascension Detail ---
export interface AscensionLead {
  leadName: string;
  leadEmail: string;
  lowTicketProduct: string;
  lowTicketDate: string;
  lowTicketCash: number;
  upsellDate: string;
  upsellProduct: string;
  upsellRevenue: number;
  upsellCash: number;
  totalInstallmentsPaid: number;
}

// --- Installment Lead ---
export interface InstallmentLead {
  leadName: string;
  leadEmail: string;
  leadId?: string;
  program: string;
  totalContract: number;
  cashPaidSoFar: number;
  lastPaymentDate: string;
  nextPaymentDate: string;
  nextPaymentAmount: number;
  isFullyPaid: boolean;
  closerName: string;
  setterName?: string;
  leadInstagram?: string;
  conversationId?: string;
  leadPhone?: string;
  daysOverdue?: number;
  isOverdue?: boolean;
  closeDate?: string;
  // Abandoned status
  isAbandoned?: boolean;
  abandonedAt?: string;
  abandonReason?: string;
  abandonNotes?: string;
  abandonCalledBy?: string;
  // PIF comparison (denormalized per-closer/setter)
  closerPifCount?: number;
  closerPifRevenue?: number;
  setterPifCount?: number;
  setterPifRevenue?: number;
  // Raw member uuids (organization_members.user_id) for active/inactive status lookup
  closerId?: string;
  setterId?: string;
}

// --- Extended KPIs ---
export interface DashboardKPIs {
  cashCollected: number;
  revenueClosed: number;
  dealsClosed: number;
  callsBooked: number; // pre-call event count (raw, ungated). For cashPerCall use settled count.
  callsTaken: number; // Shows + No-Shows: PIF + CPP + Lost + Rescheduled + SCO no_show
  shows: number; // PIF + CPP + Lost + Rescheduled (lead was present on the call)
  // Big-number no-shows = SCO no_show + orphan pre-calls. Sub-fields below expose the split.
  noShows: number;
  scoNoShows: number; // SCO with status='no_show' — closer was there, lead wasn't
  orphanPreCalls: number; // Pre-call done, no SCO ever (Mako May 13 lock)
  rescheduled: number; // SCO with rescheduled outcome — lead was present
  cancellations: number; // call_cancelled with pre-call AND no SCO (gated, not raw Calendly noise)
  preCallsMade: number; // event_type = 'sales_pre_call' — setter-qualifying form
  showUpRate: number; // shows / (shows + SCO no_shows + orphan pre-calls) × 100
  noShowRate: number; // SCO no_shows / same denominator — showUpRate + noShowRate + orphanShareRate = 100
  orphanShareRate: number; // orphan pre-calls / same denominator — the card's sub-slice
  orphanRate: number; // RPC orphan_rate: orphan pre-calls / distinct pre-call leads (f9ac0d08)
  closeRate: number; // FINAL close rate (R30, what gates score): (PIF + CPP) / decisiveCalls × 100
  pendingCloseRate: number; // provisional (R29, displayed never scored): (PIF + CPP + PendingClose) / decisiveCalls × 100
  decisiveCalls: number; // shows − rescheduled (R21/R24). shows = PIF+CPP+Lost+Rescheduled+PendingClose (R1)
  aov: number;
  cashPerCallTaken: number; // cash / shows (the SSOT "Cash / Show")
  cashPerCallBooked: number; // cash / calls_booked_settled (right-censored denominator)

  installmentsCollected: number;
  installmentsCount: number;
  installmentsSparkline: number[];
  revenueToCashRatio: string;
  slippageRate: number;

  // Ticket Tiers
  highTicket: TicketTier;
  lowTicket: TicketTier;
  ascensions: number;

  // % Changes
  cashChange: number;
  revenueChange: number;
  dealsChange: number;
  aovChange: number;
  cashPerCallTakenChange: number;
  cashPerCallBookedChange: number;
  closeRateChange: number;
  showUpRateChange: number;

  // Setting / DM Metrics
  setterBookedCalls: number;
  closerBookedCalls: number;
  totalConvosCreated: number; // raw conversations born in window — top of DM funnel
  newDMChats: number; // SSOT: lead-replied + assigned-to-setter subset of totalConvosCreated
  engaged: number; // SSOT (metricMeta.ts:166): setter sent ≥1 msg
  conversations: number;
  deepConversations: number; // 4+ setter reply turns (from setter_daily_performance)
  newDMsPerDay: number;
  newDMsPerConvo: number;
  dmConvosPerDay: number;

  // Dialing Metrics
  dials: number;
  dialsPerDay: number;
  dialsPerConvo: number;
  dialsConvoPerCall: number;

  setterQualityScore: number;
  conversationDepth: number;
  avgResponseTime: number;
}

function calcPercentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

// Outcome helpers
export function parseOutcome(callOutcome: string) {
  const outcome = (callOutcome || "").toLowerCase().replace(/_/g, " ");
  const isNoShow = outcome.includes("no show") || outcome.includes("noshow");
  const isRescheduled = outcome === "rescheduled";
  const isCanceled = outcome.includes("cancel");
  const isShow = !isNoShow && !isRescheduled && !isCanceled;
  const isInstallment = outcome.includes("paying installment") || outcome.includes("installment");
  const isLost = outcome === "lost";
  const isNewDeal =
    !isInstallment &&
    (outcome.includes("closed") ||
      outcome.includes("paid in full") ||
      outcome.includes("payment plan") ||
      outcome === "paid");
  const isPaymentPlan = outcome.includes("payment plan") || outcome.includes("closed payment plan");
  // Terminal outcome = PIF + CPP + lost + no_show.
  //
  // RENAMED from `isDecisive` on 2026-08-09. It is NOT the canonical `decisive_calls`
  // metric (Mako R21/R24), which is PIF + CPP + lost + pending_close and deliberately
  // EXCLUDES no_show. Two different sets under one word is the exact drift
  // [altara]_METRIC_RPC_SSOT.md exists to kill, so the local one gets the local name.
  // Canonical version: DECISIVE_STATUSES / isDecisiveCall() in utils/transactionStatus.ts.
  const isTerminalOutcome = isNewDeal || isLost || isNoShow;
  return {
    outcome,
    isNoShow,
    isRescheduled,
    isCanceled,
    isShow,
    isInstallment,
    isLost,
    isNewDeal,
    isPaymentPlan,
    isTerminalOutcome,
  };
}

const ZERO_KPIS: DashboardKPIs = {
  cashCollected: 0,
  revenueClosed: 0,
  dealsClosed: 0,
  callsBooked: 0,
  callsTaken: 0,
  shows: 0,
  noShows: 0,
  scoNoShows: 0,
  orphanPreCalls: 0,
  rescheduled: 0,
  cancellations: 0,
  preCallsMade: 0,
  showUpRate: 0,
  noShowRate: 0,
  orphanShareRate: 0,
  orphanRate: 0,
  closeRate: 0,
  pendingCloseRate: 0,
  decisiveCalls: 0,
  aov: 0,
  cashPerCallTaken: 0,
  cashPerCallBooked: 0,
  installmentsCollected: 0,
  installmentsCount: 0,
  installmentsSparkline: [],
  revenueToCashRatio: "0:0",
  slippageRate: 0,
  highTicket: { cash: 0, revenue: 0, deals: 0, percentChange: 0, sparkline: [] },
  lowTicket: { cash: 0, revenue: 0, deals: 0, percentChange: 0, sparkline: [] },
  ascensions: 0,
  cashChange: 0,
  revenueChange: 0,
  dealsChange: 0,
  aovChange: 0,
  cashPerCallTakenChange: 0,
  cashPerCallBookedChange: 0,
  closeRateChange: 0,
  showUpRateChange: 0,
  setterBookedCalls: 0,
  closerBookedCalls: 0,
  totalConvosCreated: 0,
  newDMChats: 0,
  engaged: 0,
  conversations: 0,
  deepConversations: 0,
  newDMsPerDay: 0,
  newDMsPerConvo: 0,
  dmConvosPerDay: 0,
  dials: 0,
  dialsPerDay: 0,
  dialsPerConvo: 0,
  dialsConvoPerCall: 0,
  setterQualityScore: 0,
  conversationDepth: 0,
  avgResponseTime: 0,
};

export const useOperationsAnalytics = () => {
  const { transactions, leaderboard, currentOrganization } = useData();
  const { dateRange, setDateRange, navigateDateRange } = useGlobalDateRange();

  // AbortController for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Server-side KPI data — combined into one state object to avoid 3 separate re-renders
  // when fetchKPIs resolves (rpcKpis, rpcPrevKpis, dailyMetrics all arrive together).
  interface RpcState {
    rpcKpis: RpcKpiData | null;
    rpcPrevKpis: RpcKpiData | null;
    dailyMetrics: DailyMetricRow[];
  }
  const [rpcState, setRpcState] = useState<RpcState>({
    rpcKpis: null,
    rpcPrevKpis: null,
    dailyMetrics: [],
  });

  // Destructure for convenient access in useMemo below
  const { rpcKpis, rpcPrevKpis, dailyMetrics } = rpcState;

  // Instagram DM stats fetched via RPC (uses same source as DM Analytics page)
  const [dmStats, setDmStats] = useState<{
    totalCreated: number; // total_new_dms: ALL conversations born in window (any source — including ManyChat blasts the lead never replied to). Top of DM funnel.
    newDMChats: number; // total_new_dms_lead_replied: SSOT (metricMeta.ts:435) — first_msg_date in range AND first_lead_msg_date IS NOT NULL AND ghl_assigned_user_id IS NOT NULL. Subset of totalCreated.
    engaged: number; // total_engaged: setter sent ≥1 msg (SSOT metricMeta.ts:166). Subset of newDMChats.
    conversations: number; // total_real_convos: legacy V1 (2+ msgs each side) — use deepConversations instead
    totalConversations: number;
    deepConversations: number; // reply_turn_deep: 4+ setter reply turns (from setter_daily_performance). "Conversations Started" on DM Analytics page.
  }>({
    totalCreated: 0,
    newDMChats: 0,
    engaged: 0,
    conversations: 0,
    totalConversations: 0,
    deepConversations: 0,
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  // Fetch Instagram DM stats via RPC (uses get_dm_analytics_fast - pre-aggregated daily_dm_metrics)
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const fetchDMStats = useCallback(async () => {
    if (!supabase || !currentOrganization?.id) {
      setDmStats({
        totalCreated: 0,
        newDMChats: 0,
        engaged: 0,
        conversations: 0,
        totalConversations: 0,
        deepConversations: 0,
      });
      return;
    }

    // Abort previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const orgId = currentOrganization.id;
      const orgTz = currentOrganization.timezone;

      // Convert picker dates to org-timezone-aware UTC boundaries
      const startISO = dateRange.start ? toOrgStartISO(dateRange.start, orgTz) : null;
      const endISO = dateRange.end ? toOrgEndISO(dateRange.end, orgTz) : null;

      // Check cache (v3 key — invalidates v2 cache. v3 added totalCreated + engaged
      // and switched newDMChats source from total_new_dms to total_new_dms_lead_replied
      // per SSOT Apr 30 redefinition + Mako 2026-05-03 ruling.)
      const cacheKey = `v3_${orgId}_${startISO}_${endISO}`;
      const cached = getOpsDmCache<typeof dmStats>(cacheKey, orgId);
      if (cached && isMountedRef.current) {
        setDmStats(cached);
        return;
      }

      const { data, error } = await supabase.rpc("get_dm_analytics_fast", {
        p_organization_id: orgId,
        p_start_date: startISO,
        p_end_date: endISO,
      });

      if (error) {
        logger.error("Failed to fetch DM stats:", error);
        return;
      }

      if (data && data.length > 0) {
        const r = data[0];
        // Deep conversations: 4+ setter reply turns (canonical per /metrics SSOT)
        // Uses total_deep_convos from setter_daily_performance.reply_turn_deep
        const deepConvos = r.total_deep_convos ?? 0;
        const dmStatsData = {
          // Top of DM funnel — ALL conversations born in window (raw, includes ManyChat).
          totalCreated: r.total_new_dms || 0,
          // NEW DMS per SSOT (metricMeta.ts:435, Apr 30 redefinition + Mako 2026-05-03 ruling):
          // lead-replied + assigned-to-setter subset of totalCreated.
          newDMChats: r.total_new_dms_lead_replied || 0,
          // ENGAGED per SSOT (metricMeta.ts:166): setter sent ≥1 message.
          engaged: r.total_engaged || 0,
          conversations: r.total_real_convos || 0, // Legacy V1 metric (kept for backward compat)
          totalConversations: (r.total_new_dms || 0) + (r.total_outbound_attempts || 0),
          deepConversations: deepConvos,
        };
        // Always cache even if unmounted — prevents cold cache on next visit
        setOpsDmCache(cacheKey, dmStatsData, orgId);
        if (isMountedRef.current) setDmStats(dmStatsData);
      }
    } catch (err) {
      // Ignore abort errors
      if ((err as Error).name === "AbortError") return;
      logger.error("Error fetching DM stats:", err);
    }
  }, [currentOrganization?.id, dateRange.start, dateRange.end]);

  useEffect(() => {
    queueMicrotask(() => fetchDMStats());
  }, [fetchDMStats]);

  // Fetch KPIs via server-side RPC (no PGRST_MAX_ROWS cap — correct for any date range)
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const fetchKPIs = useCallback(async () => {
    if (!supabase || !currentOrganization?.id || !dateRange.start || !dateRange.end) return;

    const orgId = currentOrganization.id;
    const orgTz = currentOrganization.timezone;

    // Convert picker dates to org-timezone-aware UTC boundaries
    const startISO = toOrgStartISO(dateRange.start, orgTz);
    const endISO = toOrgEndISO(dateRange.end, orgTz);

    // Previous period for % changes — same duration, ending 1ms before current start
    const duration = dateRange.end.getTime() - dateRange.start.getTime();
    const daysInRange = Math.round(duration / (24 * 60 * 60 * 1000));
    const prevEndDate = new Date(dateRange.start);
    prevEndDate.setDate(prevEndDate.getDate() - 1);
    const prevStartDate = new Date(prevEndDate);
    prevStartDate.setDate(prevStartDate.getDate() - daysInRange);

    // Cache keys
    const currKey = `curr_${orgId}_${startISO}_${endISO}`;
    const prevStartISO = toOrgStartISO(prevStartDate, orgTz);
    const prevEndISO = toOrgEndISO(prevEndDate, orgTz);

    const prevKey = `prev_${orgId}_${prevStartISO}_${prevEndISO}`;

    // Check cache — KPIs and daily metrics
    const cachedCurr = getOpsKpiCache(currKey, orgId);
    const cachedPrev = getOpsKpiCache(prevKey, orgId);
    const dailyKey = `${orgId}_${toOrgDateKey(dateRange.start)}_${toOrgDateKey(dateRange.end)}`;
    const cachedDaily = getOpsDailyCache(dailyKey, orgId);

    // If everything is cached, set state and skip network entirely
    if (cachedCurr && cachedPrev && cachedDaily) {
      if (isMountedRef.current) {
        setRpcState({ rpcKpis: cachedCurr, rpcPrevKpis: cachedPrev, dailyMetrics: cachedDaily });
      }
      return;
    }

    // Partial cache — set what we have immediately, fetch the rest
    if ((cachedCurr || cachedPrev || cachedDaily) && isMountedRef.current) {
      setRpcState((prev) => ({
        rpcKpis: cachedCurr ?? prev.rpcKpis,
        rpcPrevKpis: cachedPrev ?? prev.rpcPrevKpis,
        dailyMetrics: cachedDaily ?? prev.dailyMetrics,
      }));
    }

    try {
      // Fetch only uncached data in parallel
      const [currRes, prevRes, dailyRes] = await Promise.all([
        cachedCurr
          ? Promise.resolve({ data: cachedCurr, error: null })
          : coalesceRpc("get_operations_kpis", {
              p_organization_id: orgId,
              p_start_date: startISO,
              p_end_date: endISO,
            }),
        // D5: Skip prev-period fetch on All Time — compare window would be 730d before GHL data
        // exists, producing "−100%" everywhere. Downstream handles null rpcPrevKpis gracefully.
        dateRange.isAllTime
          ? Promise.resolve({ data: null, error: null })
          : cachedPrev
            ? Promise.resolve({ data: cachedPrev, error: null })
            : coalesceRpc("get_operations_kpis", {
                p_organization_id: orgId,
                p_start_date: prevStartISO,
                p_end_date: prevEndISO,
              }),
        cachedDaily
          ? Promise.resolve({ data: cachedDaily, error: null })
          : supabase
              .from("daily_org_metrics")
              .select(
                "date, cash_collected, revenue_closed, high_ticket_cash, low_ticket_cash, installments_collected"
              )
              .eq("organization_id", orgId)
              .gte("date", toOrgDateKey(dateRange.start))
              .lte("date", toOrgDateKey(dateRange.end))
              .order("date"),
      ]);

      // Always write to cache even if unmounted — prevents cold cache on next visit.
      const currData = currRes.data && !currRes.error ? (currRes.data as RpcKpiData) : null;
      const prevData = prevRes.data && !prevRes.error ? (prevRes.data as RpcKpiData) : null;
      const dailyData = dailyRes.data ? (dailyRes.data as DailyMetricRow[]) : null;

      if (currData && !cachedCurr) setOpsKpiCache(currKey, currData, orgId);
      if (prevData && !cachedPrev) setOpsKpiCache(prevKey, prevData, orgId);
      if (dailyData && !cachedDaily) setOpsDailyCache(dailyKey, dailyData, orgId);

      if (isMountedRef.current) {
        setRpcState((prev) => ({
          rpcKpis: currData ?? prev.rpcKpis,
          rpcPrevKpis: prevData ?? prev.rpcPrevKpis,
          dailyMetrics: dailyData ?? prev.dailyMetrics,
        }));
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      logger.error("Error fetching KPIs via RPC:", err);
    }
  }, [currentOrganization?.id, dateRange.start, dateRange.end]);

  useEffect(() => {
    queueMicrotask(() => fetchKPIs());
  }, [fetchKPIs]);

  // Boundaries are pure functions of (Date, tz) — hoist OUT of the per-row loop.
  // Old code called toOrgStartISO/toOrgEndISO per row × 28K rows = ~168K redundant
  // Intl.DateTimeFormat ops per filter run, plus 3 new Date() per row + 2 new Date()
  // per sort comparison = ~1.7M Date allocations. The GC pressure stalled the main
  // thread for hundreds of ms (invisible on flame charts — attributed to "browser
  // overhead" instead of code time). Schwartzian transform: parse once, sort numerically.
  const filteredTransactions = useMemo(() => {
    const orgTz = currentOrganization?.timezone;
    const startMs = dateRange.start
      ? new Date(toOrgStartISO(dateRange.start, orgTz)).getTime()
      : -Infinity;
    const endMs = dateRange.end ? new Date(toOrgEndISO(dateRange.end, orgTz)).getTime() : Infinity;

    const indexed: { txn: (typeof transactions)[number]; ms: number }[] = [];
    for (let i = 0; i < transactions.length; i++) {
      const t = transactions[i];
      if (!t.timestamp) continue;
      // Suppress resolved-duplicate rows (bugsweep P2). After a pending_close is
      // Converted, the original row is marked pending_superseded and a fresh terminal
      // SCO row is written — both carry sales_call_outcome + the same call_start_time,
      // so without this the Ops dashboard's embedded TransactionLog + OutcomeSummaryStrip
      // count every converted pending twice. Mirrors the standalone Transaction Log's
      // status='pending_superseded' exclusion (usePaginatedTransactions.ts).
      if (t.callOutcome === "pending_superseded") continue;
      const ms = Date.parse(t.timestamp);
      // Sentinel guard: ms < 1 day catches new Date(0) epoch-zero garbage from
      // buggy NULL coercion. Real Dynamis data is 2024+ — no false positives.
      if (Number.isNaN(ms) || ms < 86_400_000) continue;
      if (ms < startMs || ms > endMs) continue;
      indexed.push({ txn: t, ms });
    }
    indexed.sort((a, b) => b.ms - a.ms);
    return indexed.map((x) => x.txn);
  }, [transactions, dateRange, currentOrganization?.timezone]);

  // Ascension details
  const ascensionLeads = useMemo((): AscensionLead[] => {
    const sortedTxns = [...filteredTransactions].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Group by lead email
    const leadMap = new Map<string, typeof sortedTxns>();
    sortedTxns.forEach((t) => {
      const email = t.leadEmail?.toLowerCase();
      if (!email) return;
      if (!leadMap.has(email)) leadMap.set(email, []);
      leadMap.get(email)!.push(t);
    });

    const results: AscensionLead[] = [];

    leadMap.forEach((txns, email) => {
      // Find first low ticket purchase
      const lowTicket = txns.find((t) => t.eventType === "low_ticket_purchase");
      if (!lowTicket) return;

      // Find first high ticket win AFTER the low ticket
      const lowDate = new Date(lowTicket.timestamp).getTime();
      const highTicket = txns.find((t) => {
        if (t.eventType !== "sales_call_outcome") return false;
        if (new Date(t.timestamp).getTime() <= lowDate) return false;
        const { isNewDeal } = parseOutcome(t.callOutcome);
        return isNewDeal;
      });
      if (!highTicket) return;

      // Sum all installment payments for this lead
      const installments = txns.filter((t) => {
        if (t.eventType !== "sales_call_outcome") return false;
        const { isInstallment } = parseOutcome(t.callOutcome);
        return isInstallment;
      });
      const totalInstallmentsPaid = installments.reduce(
        (sum, t) => sum + (Number(t.cashCollected) || 0),
        0
      );

      results.push({
        leadName: highTicket.clientName || lowTicket.clientName || email,
        leadEmail: email,
        lowTicketProduct: lowTicket.programSold || "Low Ticket",
        lowTicketDate: lowTicket.timestamp,
        lowTicketCash: Number(lowTicket.cashCollected) || 0,
        upsellDate: highTicket.timestamp,
        upsellProduct: highTicket.programSold || "High Ticket",
        upsellRevenue: Number(highTicket.revenueClosed) || 0,
        upsellCash: Number(highTicket.cashCollected) || 0,
        totalInstallmentsPaid,
      });
    });

    return results;
  }, [filteredTransactions]);

  // KPIs — server-side RPC path only (dead client-side fallback stripped per Decision E)
  const kpis: DashboardKPIs = useMemo(() => {
    const totalDials = leaderboard.reduce((sum, e) => sum + (e.daily_dials || 0), 0);
    const uniqueDates = new Set(leaderboard.map((e) => e.date)).size || 1;
    const dialsPerDay = totalDials / uniqueDates;
    const newDMChats = dmStats.newDMChats;
    const conversations = dmStats.conversations;
    const totalConvosCreated = dmStats.totalCreated;
    const engaged = dmStats.engaged;
    const dayCount =
      dateRange.start && dateRange.end
        ? Math.max(
            1,
            Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24))
          )
        : 61;

    const htSparkline = dailyMetrics.map((d) => Number(d.high_ticket_cash) || 0);
    const ltSparkline = dailyMetrics.map((d) => Number(d.low_ticket_cash) || 0);
    const instSparkline = dailyMetrics.map((d) => Number(d.installments_collected) || 0);

    if (!rpcKpis) {
      // RPC not loaded yet — return zeroed KPIs. UI shows skeleton.
      return ZERO_KPIS;
    }

    const c = rpcKpis;
    const p = rpcPrevKpis;
    const currRates = deriveKpiRates(c);
    const prevRates = p ? deriveKpiRates(p) : null;

    const slippageRate =
      c.revenue_closed > 0 ? ((c.revenue_closed - c.cash_collected) / c.revenue_closed) * 100 : 0;
    const revenueToCashRatio =
      c.cash_collected > 0
        ? `${(c.revenue_closed / c.cash_collected).toFixed(2)}:1`
        : c.revenue_closed > 0
          ? "No Cash"
          : "0:0";

    return {
      cashCollected: c.cash_collected,
      revenueClosed: c.revenue_closed,
      dealsClosed: c.deals_closed,
      callsBooked: c.calls_booked,
      callsTaken: currRates.shows + c.no_shows,
      shows: currRates.shows,
      // Big No-Shows = SCO no_shows + orphan pre-calls (Mako May 13 lock). Sub-fields
      // expose the split so the UI can render "{N} call · {M} orphan" beneath the total.
      noShows: c.no_shows + (c.orphan_pre_calls ?? 0),
      scoNoShows: c.no_shows,
      orphanPreCalls: c.orphan_pre_calls ?? 0,
      rescheduled: c.rescheduled,
      cancellations: c.cancellations,
      preCallsMade: c.pre_calls_made,
      showUpRate: currRates.showUpRate,
      noShowRate: currRates.noShowRate,
      orphanShareRate: currRates.orphanShareRate,
      orphanRate: c.orphan_rate ?? 0,
      closeRate: currRates.closeRate,
      pendingCloseRate: currRates.pendingCloseRate,
      decisiveCalls: currRates.decisiveCalls,
      aov: currRates.aov,
      cashPerCallTaken: currRates.cashPerShow,
      cashPerCallBooked: currRates.cashPerBooked,

      installmentsCollected: c.installments_cash,
      installmentsCount: c.installments_count,
      installmentsSparkline: instSparkline,
      revenueToCashRatio,
      slippageRate,

      highTicket: {
        cash: c.ht_cash,
        revenue: c.ht_revenue,
        deals: c.ht_deals,
        percentChange: p ? calcPercentChange(c.ht_cash, p.ht_cash) : 0,
        sparkline: htSparkline,
      },
      lowTicket: {
        cash: c.lt_cash,
        revenue: c.lt_revenue,
        deals: c.lt_deals,
        percentChange: p ? calcPercentChange(c.lt_cash, p.lt_cash) : 0,
        sparkline: ltSparkline,
      },
      ascensions: ascensionLeads.length,

      cashChange: p ? calcPercentChange(c.cash_collected, p.cash_collected) : 0,
      revenueChange: p ? calcPercentChange(c.revenue_closed, p.revenue_closed) : 0,
      dealsChange: p ? c.deals_closed - p.deals_closed : 0,
      aovChange: prevRates ? currRates.aov - prevRates.aov : 0,
      cashPerCallTakenChange: prevRates ? currRates.cashPerShow - prevRates.cashPerShow : 0,
      cashPerCallBookedChange: prevRates ? currRates.cashPerBooked - prevRates.cashPerBooked : 0,
      closeRateChange: prevRates ? calcPercentChange(currRates.closeRate, prevRates.closeRate) : 0,
      showUpRateChange: prevRates
        ? calcPercentChange(currRates.showUpRate, prevRates.showUpRate)
        : 0,

      setterBookedCalls: c.setter_booked,
      closerBookedCalls: c.closer_booked,
      totalConvosCreated,
      newDMChats,
      engaged,
      conversations,
      deepConversations: dmStats.deepConversations,
      newDMsPerDay: dayCount > 0 ? newDMChats / dayCount : 0,
      newDMsPerConvo: conversations > 0 ? newDMChats / conversations : 0,
      dmConvosPerDay: dayCount > 0 ? conversations / dayCount : 0,

      dials: totalDials,
      dialsPerDay: Number(dialsPerDay.toFixed(1)),
      dialsPerConvo: conversations > 0 ? Number((totalDials / conversations).toFixed(1)) : 0,
      dialsConvoPerCall: c.calls_booked > 0 ? Number((totalDials / c.calls_booked).toFixed(1)) : 0,

      setterQualityScore: 0,
      conversationDepth: 0,
      avgResponseTime: 0,
    };
  }, [rpcKpis, rpcPrevKpis, dailyMetrics, leaderboard, dateRange, ascensionLeads, dmStats]);

  // Latest transaction timestamp (for sync indicator comparison)
  const latestTimestamp = useMemo(() => {
    if (filteredTransactions.length === 0) return null;
    return filteredTransactions[0]?.timestamp || null; // Already sorted desc
  }, [filteredTransactions]);

  // Chart Data — from daily_org_metrics (server-side, no row cap).
  // Adaptive granularity: daily over short ranges, but rolled up to weekly/monthly buckets
  // over long ranges so the line doesn't degrade into spiky noise (Mako, Jun 15 2026). The
  // rollup runs HERE (not in FinancialGraph) because this layer still has real ISO dates —
  // by the time data reaches the chart the `date` field is a lossy display string.
  const { dailyChartData, chartGranularity } = useMemo(() => {
    // 1. Normalize whichever source into ascending real-date daily points.
    let dailyPoints: DailyPoint[];
    if (dailyMetrics.length > 0) {
      dailyPoints = dailyMetrics.map((d) => ({
        ts: new Date(d.date + "T12:00:00").getTime(),
        cash: Number(d.cash_collected) || 0,
        revenue: Number(d.revenue_closed) || 0,
      }));
    } else {
      // Fallback: client-side (during initial load before daily_org_metrics returns)
      const dayMap: Record<string, DailyPoint> = {};
      filteredTransactions.forEach((t) => {
        const date = new Date(t.timestamp);
        if (isNaN(date.getTime()) || date.getFullYear() === 1970) return;
        const dayTs = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).getTime();
        const key = String(dayTs);
        if (!dayMap[key]) dayMap[key] = { ts: dayTs, cash: 0, revenue: 0 };
        dayMap[key].cash += Number(t.cashCollected) || 0;
        dayMap[key].revenue += Number(t.revenueClosed) || 0;
      });
      dailyPoints = Object.values(dayMap);
    }
    dailyPoints.sort((a, b) => a.ts - b.ts);

    // 2. Pick resolution from the selected span (fence-post inclusive); fall back to count.
    const spanDays =
      dateRange.start && dateRange.end
        ? Math.round((dateRange.end.getTime() - dateRange.start.getTime()) / 86_400_000) + 1
        : dailyPoints.length;
    const granularity = pickChartGranularity(spanDays);

    // 3. Aggregate. FinancialGraph derives the on-point value labels from cash/revenue.
    return {
      dailyChartData: aggregateDailySeries(dailyPoints, granularity),
      chartGranularity: granularity,
    };
  }, [dailyMetrics, filteredTransactions, dateRange]);

  return {
    dateRange,
    setDateRange,
    navigateDateRange,
    filteredTransactions,
    kpis,
    dailyChartData,
    chartGranularity,
    ascensionLeads,
    latestTimestamp,
  };
};
